import { useEffect } from "react";
import { Button, Group, Modal, Stack, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";

import { useProgramRegistrationParentGuardianContactUpdateMutation } from "../../api/programregistration.api";

interface ParentGuardianContactModalProps {
	opened: boolean;
	onClose: () => void;
	programSessionId: number;
	registrationId: number|undefined;
	parentGuardianId: string|undefined;
	parentGuardianName: string|undefined;
	location: string|undefined;
	mobilePhone: string|undefined;
	emergencyContact: string|undefined;
}

interface ParentGuardianContactForm {
	location: string;
	mobilePhone: string;
	emergencyContact: string;
}

const ParentGuardianContactModal = ({
	opened,
	onClose,
	programSessionId,
	registrationId,
	parentGuardianId,
	parentGuardianName,
	location,
	mobilePhone,
	emergencyContact,
}: ParentGuardianContactModalProps) => {
	const updateParentGuardianContactMutation = useProgramRegistrationParentGuardianContactUpdateMutation(programSessionId);

	const form = useForm<ParentGuardianContactForm>({
		initialValues: {
			location: "",
			mobilePhone: "",
			emergencyContact: "",
		}
	});

	useEffect(() => {
		if (!opened) {
			return;
		}

		form.setValues({
			location: location ?? "",
			mobilePhone: mobilePhone ?? "",
			emergencyContact: emergencyContact ?? "",
		});
		form.resetDirty();
		form.resetTouched();
	}, [opened, location, mobilePhone, emergencyContact]);

	const handleSubmit = (values: ParentGuardianContactForm) => {
		if (!registrationId || !parentGuardianId) {
			return;
		}

		updateParentGuardianContactMutation.mutate({
			registrationId,
			parentGuardianId,
			location: values.location,
			mobilePhone: values.mobilePhone,
			emergencyContact: values.emergencyContact,
		}, {
			onSuccess: () => {
				onClose();
			}
		});
	};

	return (
		<Modal opened={opened} onClose={onClose} title={<Text size="lg" fw="bold">Update Parent / Guardian Contact</Text>}>
			<form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
				<Stack>
					<TextInput label="Parent / Guardian" value={parentGuardianName} readOnly />
					<TextInput label="Location" placeholder="Enter location" {...form.getInputProps("location")} />
					<TextInput label="Mobile Phone" placeholder="Enter phone number" {...form.getInputProps("mobilePhone")} />
					<TextInput label="Emergency Contact" placeholder="Enter phone number" {...form.getInputProps("emergencyContact")} />
					<Group justify="end">
						<Button type="button" variant="subtle" onClick={onClose}>Cancel</Button>
						<Button type="submit" loading={updateParentGuardianContactMutation.isLoading} disabled={!form.isDirty()}>
							Save
						</Button>
					</Group>
				</Stack>
			</form>
		</Modal>
	);
}

export default ParentGuardianContactModal;
